import React, { ReactNode, useMemo } from 'react';
import * as RadixSlider from '@radix-ui/react-slider';
import { cj, cm } from 'src/utils/classNames';
import { useMergeRefs } from '@floating-ui/react';
import useSlider from 'src/components/primitives/Slider/hooks/useSlider';
import {
	BaseSliderProps,
	BaseThumbProps,
	MarksByPercentage,
} from 'src/components/primitives/Slider/types';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useThumbOffset from 'src/components/primitives/Slider/hooks/useThumbOffset';

function Slider({
	value: valueFromProps,
	onValueChange,
	onValueCommit,
	numMarks = 5,
	disabled,
	marks,
	className,
	noValueInTooltip,
	tooltipValue,
	dataTestId,
	...rest
}: BaseSliderProps) {
	const {
		value,
		isDragging,
		handleValueChange,
		handleValueCommit,
		handleLostPointerCapture,
		handleMarkClick,
		handleMarkPointerDown,
		handleMarkTouchStart,
		handleThumbPointerDown,
		thumbRef,
		trackRef,
	} = useSlider({
		onValueChange,
		onValueCommit,
		disabled,
		valueFromProps,
	});

	const marksEntries = useMemo(() => {
		// Use custom marks if provided.
		if (marks) {
			return Object.entries(marks);
		}

		// Treat one mark the same as zero marks.
		if (numMarks <= 1) {
			return;
		}

		// Else, build default marks based on the number requested.
		const marksByPercentage: MarksByPercentage = {};

		for (let i = 0; i < numMarks; i++) {
			const percentage = i / (numMarks - 1);
			const pct = Math.round(percentage * 100);
			marksByPercentage[pct] = {
				value: pct,
				label: `${pct}%`,
			};
		}

		return Object.entries(marksByPercentage);
	}, [marks, numMarks]);

	const isMobileScreenSize = useIsMobileScreenSize();
	const markWidth = isMobileScreenSize ? 2 : 4;

	return (
		<RadixSlider.Root
			className={cm(
				'relative flex h-xs w-full touch-none items-center py-3 select-none',
				disabled ? 'cursor-not-allowed' : 'cursor-pointer',
				// Marks are absolutely positioned, so we need to add extra margin for them.
				numMarks && 'mb-md',
				className
			)}
			value={value}
			onValueChange={handleValueChange}
			onValueCommit={handleValueCommit}
			onLostPointerCapture={handleLostPointerCapture}
			data-testid={dataTestId}
			{...rest}
		>
			<RadixSlider.Track
				className={cj(
					'relative grow rounded-full bg-button-secondary-bg',
					'h-2xs sm:h-xs'
				)}
				ref={trackRef}
			>
				<RadixSlider.Range className="absolute h-full rounded-full bg-button-primary-bg" />
			</RadixSlider.Track>

			{marksEntries?.map(
				([percentage, { value: markValue, label: markLabel }], index) => (
					// The mark on the track.
					<button
						key={markValue}
						className={cj(
							'group absolute top-1/2 -translate-x-1/2 -translate-y-1/2 border-none',
							'h-md w-2xs rounded-none sm:h-[14px] sm:w-xs',
							value[0] > markValue
								? 'bg-button-primary-bg'
								: 'bg-button-secondary-bg',
							disabled ? 'cursor-not-allowed' : 'cursor-pointer'
						)}
						style={{
							left: `calc(${percentage}% - ${
								index === 0 ? -(markWidth / 2) : markWidth / 2
							}px)`,
						}}
						onClick={() => handleMarkClick(markValue)}
						onPointerDown={(e) => handleMarkPointerDown(markValue, e)}
						onTouchStart={() => handleMarkTouchStart(markValue)}
					>
						{/* The label below the track mark (e.g. 10% / 10x / etc.). */}
						<div
							className={cm(
								'relative -bottom-xl block typo-body-2xs text-text-secondary transition-colors',
								disabled
									? 'cursor-not-allowed'
									: 'group-hover:text-text-default group-active:text-text-default',
								// Highlight the mark if it's non-zero and the current value.
								value[0] > 0 && value[0] === markValue && 'text-text-default',
								index > 0 && '-left-1',
								// Check `index > 0` otherwise we'll incorrectly add this to the
								// first mark when there's only one mark.
								index > 0 &&
									index === marksEntries.length - 1 &&
									getLastMarkLabelLeftPosition(markLabel)
							)}
						>
							{markLabel}
						</div>
					</button>
				)
			)}

			<RadixSlider.Thumb asChild>
				<Thumb
					value={value[0]}
					isDragging={isDragging}
					onPointerDown={handleThumbPointerDown}
					noValueInTooltip={noValueInTooltip}
					tooltipValue={tooltipValue}
					disabled={disabled}
					thumbRef={thumbRef}
					trackRef={trackRef}
				/>
			</RadixSlider.Thumb>
		</RadixSlider.Root>
	);
}

const THUMB_WRAPPER_STYLE = {
	// Radix by default applies its own thumb offset based off the width
	// but since ours is a special snowflake we need to override it, so we
	// lie to Radix here and tell it the thumb has `0` width.
	width: 0,
};

const Thumb = ({
	value,
	isDragging,
	onPointerDown,
	noValueInTooltip,
	tooltipValue,
	disabled,
	thumbRef,
	trackRef,
	ref,
	...rest
}: BaseThumbProps) => {
	const mergedRef = useMergeRefs([ref, thumbRef]);
	const translateXPx = useThumbOffset({ value, trackRef });

	return (
		<span
			ref={mergedRef}
			onPointerDown={onPointerDown}
			className="absolute"
			style={THUMB_WRAPPER_STYLE}
			{...rest}
		>
			<div
				className={cj(
					'absolute box-content size-[14px] -translate-y-1/2 rounded-xs sm:size-lg sm:rounded-sm',
					'border-2 border-button-primary-bg bg-container-bg',
					disabled && 'cursor-not-allowed'
				)}
				style={{ transform: `translateX(${translateXPx}px)` }}
			>
				{!noValueInTooltip && (
					<div
						className={cm(
							// `pointer-events-none` so that clicking it (which can happen on
							// accident when its opacity is 0) does not trigger the slider thumb.
							'pointer-events-none relative -top-[24px] w-[38px] sm:-top-[28px]',
							'flex justify-center rounded-xs bg-tooltip-bg p-xs typo-body-2xs text-text-default',
							'opacity-0 transition-opacity delay-200',
							// X-axis offset when close to the edges of the container.
							value <= 10 && '-translate-x-xs',
							value > 90 && '-translate-x-1/2',
							// X-axis offset for everything in between.
							value > 10 && value <= 90 && '-translate-x-md',
							// Only show once we're dragging.
							isDragging && 'opacity-100'
						)}
					>
						{tooltipValue ?? `${Math.round(value)}%`}
					</div>
				)}
			</div>
		</span>
	);
};

// The last mark can vary in length / width, so determing a correct left position
// is tricky. Doing this for the time being but we should find a better solution.
const getLastMarkLabelLeftPosition = (markLabel: ReactNode) => {
	// Only handling string cases for now.
	if (!markLabel || typeof markLabel !== 'string') {
		return null;
	}

	const length = markLabel.length;

	if (length <= 2) {
		return '-left-2';
	}

	if (length === 3) {
		return '-left-4';
	}

	if (length === 4) {
		return '-left-6';
	}
};

export default React.memo(Slider);
